import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuthStore } from '../store/authStore';
import { tokensService, TokenSummary } from '../services/tokens';
import { Language, Person, AdminPanelSettings, Menu, Close, Add } from '@mui/icons-material';
import ThemeToggle from './ThemeToggle';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [tokenSummary, setTokenSummary] = useState<TokenSummary | null>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ];

  const currentLang = languages.find(l => l.code === language) || languages[1];
  const isAdmin = user?.roles?.includes('admin') || false;

  const loadTokenSummary = useCallback(async () => {
    if (!isAuthenticated) {
      setTokenSummary(null);
      return;
    }
    try {
      const summary = await tokensService.getMyTokens();
      setTokenSummary(summary);
    } catch (err) {
      console.error('Failed to load token summary:', err);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadTokenSummary();
  }, [loadTokenSummary]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) setShowLangMenu(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) setShowMobileMenu(false);
    };
    if (showLangMenu || showMobileMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLangMenu, showMobileMenu]);

  const totalTokens = tokenSummary ? tokenSummary.tokens_remaining + tokenSummary.tokens_used : 0;

  const navLinkClass = "text-xs text-headings hover:opacity-70 transition-opacity tracking-tight font-medium";
  const ctaClass = "text-xs font-medium text-white bg-headings dark:bg-headings dark:text-gray-900 px-2.5 py-1.5 rounded-full hover:opacity-90 transition-opacity tracking-tight shrink-0";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pt-2 px-2 md:pt-2.5 md:px-3">
      {/* Kartlarla uyumlu pill: yuvarlak köşeler, boy uzun */}
      <div className="max-w-6xl mx-auto bg-gray-100 dark:bg-black/95 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-800/50">
        <div className="flex items-center justify-between px-5 py-2 md:px-8 md:py-2">
          {/* Sol: Logo */}
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer shrink-0"
            >
              <img
                src="/mr-mirror-logo.png"
                alt="Mr.Mirror"
                className="h-7 md:h-8 w-auto object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className="text-sm md:text-base font-bold tracking-tight text-headings">
                Mr.Mirror®
              </span>
            </button>
          </div>

          {/* Sağ: Linkler + CTA + Menü ikonu */}
          <nav className="hidden md:flex items-center gap-4 lg:gap-5">
            {isAuthenticated ? (
              <>
                <button onClick={() => navigate('/dashboard')} className={navLinkClass}>{t.analyzeTitle}</button>
                <button onClick={() => navigate('/upload')} className={navLinkClass}>{language === 'tr' ? 'Yeni Analiz' : 'New Analysis'}</button>
                <button
                  onClick={() => navigate('/profile')}
                  className={`${navLinkClass} flex items-center gap-1.5 min-w-0 max-w-[140px] lg:max-w-[200px]`}
                  title={user?.name || user?.username || ''}
                >
                  <Person sx={{ fontSize: 16 }} className="shrink-0" />
                  <div className="flex flex-col items-start text-left min-w-0">
                    <span className="truncate w-full">{user?.name || user?.username || (language === 'tr' ? 'Profil' : 'Profile')}</span>
                    {tokenSummary != null && (
                      <span className="text-xs text-main/70 font-normal">
                        {tokenSummary.tokens_remaining} / {totalTokens} {language === 'tr' ? 'kalan' : 'remaining'}
                      </span>
                    )}
                  </div>
                </button>
                {isAdmin && (
                  <button onClick={() => navigate('/admin')} className={navLinkClass} title={t.admin}>
                    <AdminPanelSettings sx={{ fontSize: 16 }} />
                  </button>
                )}
              </>
            ) : (
              <>
                <button onClick={() => navigate('/features')} className={navLinkClass}>{language === 'tr' ? 'Özellikler' : 'Features'}</button>
                <button onClick={() => navigate('/pricing')} className={navLinkClass}>{t.pricing}</button>
                <button onClick={() => navigate('/blog')} className={navLinkClass}>{t.blog}</button>
                <button onClick={() => navigate('/faq')} className={navLinkClass}>{t.faq}</button>
              </>
            )}

            {/* Dil seçici */}
            <div className="relative" ref={langMenuRef}>
              <button
                type="button"
                onClick={() => setShowLangMenu(!showLangMenu)}
                className={`${navLinkClass} flex items-center gap-1`}
                title={currentLang.name}
              >
                <span className="text-sm">{currentLang.flag}</span>
              </button>
              {showLangMenu && (
                <div className="absolute right-0 mt-1 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg py-1 z-50">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => { setLanguage(lang.code as any); setShowLangMenu(false); }}
                      className={`w-full px-2.5 py-1.5 text-left text-xs transition-colors flex items-center gap-1.5 rounded-lg mx-0.5 ${
                        language === lang.code
                          ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-gray-100 font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Tema seçimi: 3'lü segmented toggle */}
            <ThemeToggle size="sm" />

            {/* CTA buton - görseldeki siyah dolgu, yuvarlak köşe */}
            {isAuthenticated ? (
              <button
                onClick={() => { logout(); navigate('/'); }}
                className={navLinkClass}
              >
                {t.logout}
              </button>
            ) : (
              <button onClick={() => navigate('/login')} className={ctaClass}>
                {language === 'tr' ? 'Proje başlat' : 'Start a project'}
              </button>
            )}
          </nav>

          {/* Mobil: CTA veya menü + pill menü ikonu */}
          <div className="md:hidden flex items-center gap-1.5">
            {!isAuthenticated ? (
              <button onClick={() => navigate('/login')} className={ctaClass}>
                {language === 'tr' ? 'Proje başlat' : 'Start a project'}
              </button>
            ) : null}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="w-7 h-7 rounded-full bg-headings dark:bg-headings flex items-center justify-center text-white dark:text-gray-900 hover:opacity-90 transition-opacity shrink-0"
              aria-label="Menu"
            >
              {showMobileMenu ? <Close sx={{ fontSize: 18 }} /> : <Add sx={{ fontSize: 18 }} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobil açılır menü - pill altında */}
      {showMobileMenu && (
        <div ref={mobileMenuRef} className="md:hidden absolute top-full left-2 right-2 mt-1 bg-gray-100 dark:bg-black/95 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-800 py-2 px-3 shadow-lg">
          {!isAuthenticated && (
            <>
              <button onClick={() => { navigate('/features'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{language === 'tr' ? 'Özellikler' : 'Features'}</button>
              <button onClick={() => { navigate('/pricing'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{t.pricing}</button>
              <button onClick={() => { navigate('/blog'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{t.blog}</button>
              <button onClick={() => { navigate('/faq'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{t.faq}</button>
            </>
          )}
          {isAuthenticated && (
            <>
              <button onClick={() => { navigate('/dashboard'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{t.analyzeTitle}</button>
              <button onClick={() => { navigate('/upload'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{language === 'tr' ? 'Yeni Analiz' : 'New Analysis'}</button>
              <button onClick={() => { navigate('/profile'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{user?.name || user?.username || (language === 'tr' ? 'Profil' : 'Profile')}</button>
              {isAdmin && <button onClick={() => { navigate('/admin'); setShowMobileMenu(false); }} className={`block w-full text-left py-1.5 px-2 rounded-lg ${navLinkClass}`}>{t.admin}</button>}
            </>
          )}
          <div className="flex items-center gap-2 pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={() => setShowLangMenu(!showLangMenu)} className={`py-1.5 px-2 rounded-lg ${navLinkClass} text-main`}>
              {currentLang.flag} {currentLang.name}
            </button>
            <ThemeToggle size="sm" />
            {isAuthenticated && (
              <button onClick={() => { logout(); navigate('/'); setShowMobileMenu(false); }} className={`py-1.5 px-2 rounded-lg ${navLinkClass}`}>{t.logout}</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
